import { PartnerCompletenessTranslations } from '../configs/translations'
import type { ICompletenessItem, ICompletenessProfile } from '../model/types'

/**
 * The completeness rows of one profile, in the order the widget lists them. Rows the profile
 * carries no number for are dropped, so a profile only shows the sections it is measured on.
 */
export const getCompletenessItems = (
  completeness: ICompletenessProfile | undefined,
  isDistributor: boolean
): ICompletenessItem[] => {
  if (!completeness) {
    return []
  }

  const sobPage = isDistributor ? '11' : '10'
  const actionPlanPage = isDistributor ? '8' : '6'

  const items: ICompletenessItem[] = [
    {
      id: 'basic',
      label: PartnerCompletenessTranslations.BasicInfo,
      data: completeness?.basicInfo,
      color: 'rgba(0, 179, 227, 0.2)',
      scrollElementId: 'WidgetPartnerBasicInfo',
    },
    {
      id: 'contact',
      label: PartnerCompletenessTranslations.ContactInfo,
      data: completeness?.contacts,
      color: 'rgba(235, 69, 99, 0.2)',
      scrollElementId: 'ContactsTable',
    },
    {
      id: 'swotAnalysis',
      label: PartnerCompletenessTranslations.SwotAnalysis,
      data: completeness?.swot,
      color: 'rgba(255, 191, 38, 0.2)',
      urlParams: {
        'jbp-tab': '16',
      },
    },
    {
      id: 'devicePrint',
      label: PartnerCompletenessTranslations.DeviceInfoPrint,
      data: completeness?.deviceInfoPrint,
      color: 'rgba(250, 224, 71, 0.2)',
      urlParams: {
        'jbp-tab': '2',
      },
    },
    {
      id: 'device',
      label: PartnerCompletenessTranslations.DeviceInfo,
      data: completeness?.deviceInfo,
      color: 'rgba(250, 224, 71, 0.2)',
      urlParams: {
        'jbp-tab': '1',
      },
    },
    {
      id: 'shareOfBusiness',
      label: PartnerCompletenessTranslations.ShareOfBusiness,
      data: completeness?.shareOfBusiness,
      color: 'rgba(99, 111, 68, 0.2)',
      urlParams: {
        'jbp-tab': sobPage,
      },
    },
    {
      id: 'market',
      label: PartnerCompletenessTranslations.MarketInfo,
      data: completeness?.marketInfo,
      color: 'rgba(125, 56, 148, 0.2)',
      urlParams: {
        'jbp-tab': '2',
      },
    },
    {
      id: 'solutions',
      label: PartnerCompletenessTranslations.SolutionsInfo,
      data: completeness?.solutions,
      color: 'rgba(228, 136, 38, 0.2)',
      urlParams: {
        'jbp-tab': '13',
      },
    },
    {
      id: 'MPS',
      label: PartnerCompletenessTranslations.MPSStatusInfo,
      data: completeness?.mps,
      color: 'rgba(174, 198, 112, 0.2)',
      urlParams: {
        'jbp-tab': '20',
        fct: 'jbp-print-officehw',
      },
    },
    {
      id: 'PrintServices',
      label: PartnerCompletenessTranslations.PrintServicesStatusInfo,
      data: completeness?.printServices,
      color: 'rgba(245, 187, 44, 0.4)',
      urlParams: {
        'jbp-tab': '20',
        fct: 'jbp-print-workforce',
      },
    },
    {
      id: 'PersonalSystemsServices',
      label: PartnerCompletenessTranslations.PersonalSystemsServicesStatusInfo,
      data: completeness?.personalSystemsServices,
      color: 'rgba(51, 131, 9, 0.2)',
      urlParams: {
        'jbp-tab': '20',
        fct: 'jbp-pc-commercialservices',
      },
    },
    {
      id: 'poly',
      label: PartnerCompletenessTranslations.Poly,
      data: completeness?.poly,
      color: 'rgba(179, 240, 207, 0.9)',
      urlParams: {
        'jbp-tab': '20',
        fct: 'jbp-pc-poly',
      },
    },
    {
      id: 'mif',
      label: PartnerCompletenessTranslations.MIF,
      data: completeness?.mif,
      color: 'rgba(201, 197, 35, 0.2)',
      urlParams: {
        'jbp-tab': '20',
        fct: 'jbp-mif-replacement',
      },
    },
    {
      id: 'mpsSubs',
      label: PartnerCompletenessTranslations.MPSSubs,
      data: completeness?.mpsSubs,
      color: 'rgba(161, 122, 255, 0.2)',
      urlParams: {
        'jbp-tab': '20',
        fct: 'jbp-mps-subscription',
      },
    },
    {
      id: 'rooms',
      label: PartnerCompletenessTranslations.Rooms,
      data: completeness?.rooms,
      color: 'rgba(47, 45, 65, 0.2)',
      urlParams: {
        'jbp-tab': '20',
        fct: 'jbp-pc-room',
      },
    },
    {
      id: 'aipc',
      label: PartnerCompletenessTranslations.AIPC,
      data: completeness?.aipc,
      color: 'rgba(213, 58, 204, 0.2)',
      urlParams: {
        'jbp-tab': '20',
        fct: 'jbp-pc-aipc',
      },
    },
    {
      id: 'finSobPlan',
      label: PartnerCompletenessTranslations.FinSobPlan,
      data: completeness?.finSobPlan,
      color: 'rgba(65, 105, 225, 0.2)',
      urlParams: {
        'jbp-tab': '20',
        fct: 'jbp-planning-total',
      },
    },
    {
      id: 'planning',
      label: PartnerCompletenessTranslations.Planning,
      data: completeness?.planning,
      color: 'rgba(65, 105, 225, 0.2)',
      urlParams: {
        'jbp-tab': '3',
        'hr-tab': '2',
      },
    },
    {
      id: 'assortment',
      label: PartnerCompletenessTranslations.Assortment,
      data: completeness?.assortment,
      color: 'rgba(204, 249, 205, 1)',
    },
    {
      id: 'initiativesPlan',
      label: PartnerCompletenessTranslations.InitiativesPlan,
      data: completeness?.initiativesPlan,
      color: 'rgba(45, 191, 111, 0.2)',
      urlParams: {
        'jbp-tab': '21',
      },
    },
    {
      id: 'actions',
      label: PartnerCompletenessTranslations.ActionsPlan,
      data: completeness?.actionsPlan,
      color: 'rgba(45, 191, 111, 0.2)',
      urlParams: {
        'jbp-tab': actionPlanPage,
      },
    },
    {
      id: 'masterDealer',
      label: PartnerCompletenessTranslations.MasterDealer,
      data: completeness?.masterDealer,
      color: 'rgba(255, 160, 122, 0.2)',
      urlParams: {
        'jbp-tab': '9',
      },
    },
    {
      id: 'inventoryManagement',
      label: PartnerCompletenessTranslations.InventoryManagement,
      data: completeness?.inventory,
      color: 'rgba(125, 56, 148, 0.2)',
      urlParams: {
        'jbp-tab': '14',
      },
    },
    {
      id: 'creditStatus',
      label: PartnerCompletenessTranslations.CreditStatusInfo,
      data: completeness?.creditStatusInfo,
      color: 'rgba(255, 160, 122, 0.2)',
      urlParams: {
        'jbp-tab': '15',
      },
    },
  ]

  return items.filter((it) => it.data !== undefined)
}
